import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';

// Types for AI metadata extraction
export interface AIMetadataExtractionOptions {
  useAI?: boolean;
  extractThumbnail?: boolean;
  analyzeContent?: boolean;
  generateKeywords?: boolean;
  enhanceExisting?: boolean;
}

export interface AIMetadataExtraction {
  id: string;
  media_id: string;
  tenant_id: string;
  extraction_type: string;
  extracted_metadata: Record<string, any>;
  ai_enhanced_metadata: AIEnhancedMetadata;
  content_analysis: ContentAnalysis;
  thumbnail_info?: ThumbnailInfo;
  created_at: string;
  extraction_date: string;
}

export interface AIEnhancedMetadata {
  ai_generated_title?: string;
  ai_generated_description?: string;
  ai_generated_keywords: string[];
  detected_language?: string;
  content_category?: string;
  mood_tags: string[];
  style_tags: string[];
  technical_quality?: {
    score: number;
    factors: string[];
    recommendations: string[];
  };
  accessibility_features: string[];
  content_warnings: string[];
  suggested_metadata: Record<string, any>;
}

export interface ContentAnalysis {
  analysis_date: string;
  confidence_scores: Record<string, number>;
  detected_objects: Array<{
    object: string;
    confidence: number;
    timestamp?: number;
    bounding_box?: number[];
  }>;
  detected_text: Array<{
    text: string;
    confidence: number;
    timestamp?: number;
    bounding_box?: number[];
    page?: number;
  }>;
  detected_faces: Array<{
    face_id: string;
    confidence: number;
    timestamp?: number;
    age_range?: [number, number];
    gender?: string;
    bounding_box?: number[];
  }>;
  scene_analysis?: Array<{
    scene_type: string;
    confidence: number;
    start_time: number;
    end_time: number;
  }>;
  audio_analysis?: {
    speech_detected: boolean;
    music_detected: boolean;
    language: string;
    sentiment: string;
    volume_levels: {
      avg: number;
      max: number;
      min: number;
    };
    genre?: string;
    mood?: string;
    tempo?: number;
    key?: string;
    instruments?: string[];
    quality?: string;
  };
  content_rating?: string;
  quality_score?: number;
}

export interface ThumbnailInfo {
  path: string;
  width: number;
  height: number;
  format: string;
  size?: number;
}

export interface AICapabilities {
  aiServices: {
    openai: {
      enabled: boolean;
      features: string[];
    };
    google: {
      enabled: boolean;
      features: string[];
    };
    aws: {
      enabled: boolean;
      features: string[];
    };
  };
  supportedMediaTypes: string[];
  extractionFeatures: string[];
  rateLimits: {
    extractionsPerHour: number;
    keywordGenerationPerHour: number;
  };
}

export interface ExtractionResult {
  extractionId: string;
  metadata: {
    basic: Record<string, any>;
    aiEnhanced: AIEnhancedMetadata;
    contentAnalysis: ContentAnalysis;
    thumbnails: ThumbnailInfo[];
  };
  extractedAt: string;
}

export interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  details?: any[];
}

// Helper function to get tenant ID
const getTenantId = (): string => {
  return localStorage.getItem('tenantId') || process.env.NEXT_PUBLIC_DEFAULT_TENANT_ID || '';
};

// API service functions
const aiMetadataApi = {
  extractMetadata: async (
    mediaId: string,
    options: AIMetadataExtractionOptions = {}
  ): Promise<ExtractionResult> => {
    const tenantId = getTenantId();
    
    const response = await fetch(`/api/ai-metadata/${mediaId}/extract`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantId,
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify(options)
    });

    if (!response.ok) {
      throw new Error(`AI metadata extraction failed: ${response.statusText}`);
    }

    const result: APIResponse<ExtractionResult> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'AI metadata extraction failed');
    }

    return result.data;
  },

  getExtractionHistory: async (
    mediaId: string,
    limit = 10,
    offset = 0
  ): Promise<{ extractions: AIMetadataExtraction[]; pagination: any }> => {
    const tenantId = getTenantId();
    
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString()
    });

    const response = await fetch(`/api/ai-metadata/${mediaId}/extractions?${params}`, {
      headers: {
        'X-Tenant-ID': tenantId,
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get extraction history: ${response.statusText}`);
    }

    const result: APIResponse<{ extractions: AIMetadataExtraction[]; pagination: any }> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to get extraction history');
    }

    return result.data;
  },

  getExtractionDetails: async (
    mediaId: string,
    extractionId: string
  ): Promise<AIMetadataExtraction> => {
    const tenantId = getTenantId();
    
    const response = await fetch(`/api/ai-metadata/${mediaId}/extractions/${extractionId}`, {
      headers: {
        'X-Tenant-ID': tenantId,
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get extraction details: ${response.statusText}`);
    }

    const result: APIResponse<AIMetadataExtraction> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to get extraction details');
    }

    return result.data;
  },

  generateKeywords: async (mediaId: string): Promise<{ keywords: string[]; generatedAt: string }> => {
    const tenantId = getTenantId();
    
    const response = await fetch(`/api/ai-metadata/${mediaId}/generate-keywords`, {
      method: 'POST',
      headers: {
        'X-Tenant-ID': tenantId,
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });

    if (!response.ok) {
      throw new Error(`Keyword generation failed: ${response.statusText}`);
    }

    const result: APIResponse<{ keywords: string[]; generatedAt: string }> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Keyword generation failed');
    }

    return result.data;
  },

  enhanceTitle: async (mediaId: string): Promise<{
    originalTitle: string;
    enhancedTitle: string;
    suggestions: string[];
    generatedAt: string;
  }> => {
    const tenantId = getTenantId();
    
    const response = await fetch(`/api/ai-metadata/${mediaId}/enhance-title`, {
      method: 'POST',
      headers: {
        'X-Tenant-ID': tenantId,
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });

    if (!response.ok) {
      throw new Error(`Title enhancement failed: ${response.statusText}`);
    }

    const result: APIResponse<{
      originalTitle: string;
      enhancedTitle: string;
      suggestions: string[];
      generatedAt: string;
    }> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Title enhancement failed');
    }

    return result.data;
  },

  getCapabilities: async (): Promise<AICapabilities> => {
    const response = await fetch('/api/ai-metadata/capabilities', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get AI capabilities: ${response.statusText}`);
    }

    const result: APIResponse<AICapabilities> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to get AI capabilities');
    }

    return result.data;
  },

  batchExtract: async (
    mediaIds: string[],
    options: AIMetadataExtractionOptions = {}
  ): Promise<{
    results: Array<{
      mediaId: string;
      success: boolean;
      extractionId?: string;
      error?: string;
    }>;
    summary: {
      total: number;
      successful: number;
      failed: number;
    };
    processedAt: string;
  }> => {
    const tenantId = getTenantId();
    
    const response = await fetch('/api/ai-metadata/batch-extract', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantId,
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({ mediaIds, options })
    });

    if (!response.ok) {
      throw new Error(`Batch extraction failed: ${response.statusText}`);
    }

    const result: APIResponse<{
      results: Array<{
        mediaId: string;
        success: boolean;
        extractionId?: string;
        error?: string;
      }>;
      summary: {
        total: number;
        successful: number;
        failed: number;
      };
      processedAt: string;
    }> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Batch extraction failed');
    }

    return result.data;
  }
};

/**
 * Hook for AI metadata extraction and management
 */
export const useAIMetadata = (mediaId: string) => {
  const queryClient = useQueryClient();
  const [isExtracting, setIsExtracting] = useState(false);

  // Extract metadata mutation
  const extractMetadataMutation = useMutation({
    mutationFn: (options: AIMetadataExtractionOptions) => 
      aiMetadataApi.extractMetadata(mediaId, options),
    onMutate: () => {
      setIsExtracting(true);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['aiMetadata', mediaId] });
      queryClient.invalidateQueries({ queryKey: ['mediaRecord', mediaId] });
      toast.success('AI metadata extraction completed successfully');
    },
    onError: (error: Error) => {
      toast.error(`AI metadata extraction failed: ${error.message}`);
    },
    onSettled: () => {
      setIsExtracting(false);
    }
  });

  // Generate keywords mutation
  const generateKeywordsMutation = useMutation({
    mutationFn: () => aiMetadataApi.generateKeywords(mediaId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mediaRecord', mediaId] });
      toast.success(`Generated ${data.keywords.length} keywords`);
    },
    onError: (error: Error) => {
      toast.error(`Keyword generation failed: ${error.message}`);
    }
  });

  // Enhance title mutation
  const enhanceTitleMutation = useMutation({
    mutationFn: () => aiMetadataApi.enhanceTitle(mediaId),
    onSuccess: (data) => {
      toast.success('Title enhancement completed');
    },
    onError: (error: Error) => {
      toast.error(`Title enhancement failed: ${error.message}`);
    }
  });

  // Get extraction history query
  const {
    data: extractionHistory,
    isLoading: isLoadingHistory,
    error: historyError,
    refetch: refetchHistory
  } = useQuery({
    queryKey: ['aiMetadata', mediaId, 'history'],
    queryFn: () => aiMetadataApi.getExtractionHistory(mediaId),
    enabled: !!mediaId,
    staleTime: 300000, // 5 minutes
  });

  // Helper functions
  const extractMetadata = useCallback(
    (options: AIMetadataExtractionOptions = {}) => {
      return extractMetadataMutation.mutateAsync(options);
    },
    [extractMetadataMutation]
  );

  const generateKeywords = useCallback(() => {
    return generateKeywordsMutation.mutateAsync();
  }, [generateKeywordsMutation]);

  const enhanceTitle = useCallback(() => {
    return enhanceTitleMutation.mutateAsync();
  }, [enhanceTitleMutation]);

  return {
    // Data
    extractionHistory: extractionHistory?.extractions || [],
    pagination: extractionHistory?.pagination,
    
    // Loading states
    isExtracting: isExtracting || extractMetadataMutation.isPending,
    isGeneratingKeywords: generateKeywordsMutation.isPending,
    isEnhancingTitle: enhanceTitleMutation.isPending,
    isLoadingHistory,
    
    // Error states
    extractionError: extractMetadataMutation.error,
    keywordError: generateKeywordsMutation.error,
    titleError: enhanceTitleMutation.error,
    historyError,
    
    // Actions
    extractMetadata,
    generateKeywords,
    enhanceTitle,
    refetchHistory,
    
    // Results
    lastExtraction: extractMetadataMutation.data,
    lastKeywords: generateKeywordsMutation.data,
    lastTitleEnhancement: enhanceTitleMutation.data
  };
};

/**
 * Hook for AI capabilities
 */
export const useAICapabilities = () => {
  return useQuery({
    queryKey: ['aiCapabilities'],
    queryFn: aiMetadataApi.getCapabilities,
    staleTime: 600000, // 10 minutes
    gcTime: 3600000, // 1 hour
  });
};

/**
 * Hook for batch operations
 */
export const useBatchAIMetadata = () => {
  const queryClient = useQueryClient();

  const batchExtractMutation = useMutation({
    mutationFn: ({ mediaIds, options }: { mediaIds: string[]; options?: AIMetadataExtractionOptions }) =>
      aiMetadataApi.batchExtract(mediaIds, options),
    onSuccess: (data) => {
      // Invalidate queries for all processed media
      data.results.forEach(result => {
        if (result.success) {
          queryClient.invalidateQueries({ queryKey: ['aiMetadata', result.mediaId] });
          queryClient.invalidateQueries({ queryKey: ['mediaRecord', result.mediaId] });
        }
      });
      
      toast.success(
        `Batch extraction completed: ${data.summary.successful} successful, ${data.summary.failed} failed`
      );
    },
    onError: (error: Error) => {
      toast.error(`Batch extraction failed: ${error.message}`);
    }
  });

  const batchExtract = useCallback(
    (mediaIds: string[], options: AIMetadataExtractionOptions = {}) => {
      return batchExtractMutation.mutateAsync({ mediaIds, options });
    },
    [batchExtractMutation]
  );

  return {
    batchExtract,
    isBatchExtracting: batchExtractMutation.isPending,
    batchError: batchExtractMutation.error,
    lastBatchResult: batchExtractMutation.data
  };
};

export default useAIMetadata;